import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, ChevronDown, Loader2 } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import VideoBackdrop from './VideoBackdrop'
import LangSelect from './LangSelect'

// Motivos de recusa que o backend devolve no ?auth_error= do redirect. São códigos
// curtos de propósito: a mensagem detalhada fica no log e na trilha de auditoria, nunca
// na tela, para não virar oráculo de quem existe e quem não existe.
function errorKey(code) {
  switch (code) {
    case 'not_allowed': return 'auth:errors.not_allowed'
    case 'cancelled': return 'auth:errors.cancelled'
    case 'expired': return 'auth:errors.expired'
    case 'state': return 'auth:errors.state'
    case 'invalid_credentials': return 'auth:errors.invalid_credentials'
    case 'origin': return 'auth:errors.origin'
    case null: case undefined: case '': return null
    default: return 'auth:errors.generic'
  }
}

export default function Login() {
  const { t } = useTranslation()
  const { config, loginGoogle, loginLocal } = useAuth()
  const [showLocal, setShowLocal] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  // Lido UMA vez e apagado da URL: deixado lá, o banner do Google fica permanente e
  // reaparece a cada erro do formulário local, porque a condição é `error || redirectError`.
  const [redirectError] = useState(() =>
    errorKey(new URLSearchParams(window.location.search).get('auth_error')))
  const [dismissedRedirect, setDismissedRedirect] = useState(false)
  useEffect(() => {
    const u = new URL(window.location.href)
    if (u.searchParams.has('auth_error')) {
      u.searchParams.delete('auth_error')
      window.history.replaceState({}, '', u)
    }
  }, [])

  const submit = async (e) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setDismissedRedirect(true)
    try {
      await loginLocal(email, password)
    } catch (err) {
      setError(t(errorKey(err.code) || 'auth:errors.generic'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="relative flex min-h-screen">
      <VideoBackdrop pauseLabel={t('auth:video.pause')} playLabel={t('auth:video.play')} />

      <main className="pointer-events-none relative z-10 flex w-full items-center justify-center px-6 py-10 lg:w-[560px] lg:justify-start lg:px-16">
        <div className="pointer-events-auto w-full max-w-sm">
          <img src="/carcosa-logo-dark.svg" alt="Carcosa Secrets" className="mb-10 h-7" />

          <h1 className="text-2xl font-semibold text-white">{t('auth:title')}</h1>
          <p className="mt-2 text-sm text-slate-300">{t('auth:subtitle')}</p>

          {(error || (redirectError && !dismissedRedirect)) && (
            <div className="mt-6 flex items-start gap-2 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-200">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error || t(redirectError)}</span>
            </div>
          )}

          {config?.google && (
            <button
              type="button"
              onClick={loginGoogle}
              className="mt-8 flex w-full items-center justify-center gap-3 rounded-lg bg-white px-4 py-3
                         text-sm font-semibold text-slate-800 shadow-lg transition hover:bg-slate-100
                         focus:outline-none focus:ring-2 focus:ring-white/60"
            >
              <GoogleMark />
              {t('auth:google')}
            </button>
          )}

          {config?.local && (
            <>
              <button
                type="button"
                onClick={() => setShowLocal(v => !v)}
                aria-expanded={showLocal}
                aria-controls="login-local-form"
                className="mt-6 flex w-full items-center justify-center gap-1 text-xs text-slate-300 transition hover:text-white"
              >
                {t('auth:other_options')}
                <ChevronDown size={14} className={`transition-transform ${showLocal ? 'rotate-180' : ''}`} />
              </button>

              {showLocal && (
                <form id="login-local-form" onSubmit={submit} className="mt-5 space-y-3">
                  <div>
                    <label htmlFor="login-email" className="mb-1.5 block text-xs font-medium text-slate-300">
                      {t('auth:email')}
                    </label>
                    <input
                      id="login-email"
                      type="email"
                      autoComplete="username"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      className="h-10 w-full rounded-lg border border-white/15 bg-white/10 px-3 text-sm text-white
                                 placeholder:text-slate-400 backdrop-blur focus:border-brand-400 focus:outline-none
                                 focus:ring-2 focus:ring-brand-400/30"
                    />
                  </div>
                  <div>
                    <label htmlFor="login-password" className="mb-1.5 block text-xs font-medium text-slate-300">
                      {t('auth:password')}
                    </label>
                    <input
                      id="login-password"
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      className="h-10 w-full rounded-lg border border-white/15 bg-white/10 px-3 text-sm text-white
                                 placeholder:text-slate-400 backdrop-blur focus:border-brand-400 focus:outline-none
                                 focus:ring-2 focus:ring-brand-400/30"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={busy}
                    className="flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-brand-600 text-sm
                               font-semibold text-white transition hover:bg-brand-500 disabled:opacity-60
                               focus:outline-none focus:ring-2 focus:ring-brand-400/40"
                  >
                    {busy && <Loader2 size={15} className="animate-spin" />}
                    {t('auth:sign_in')}
                  </button>
                </form>
              )}
            </>
          )}

          {!config?.google && !config?.local && (
            <p className="mt-8 text-sm text-slate-300">{t('auth:no_methods')}</p>
          )}

          <div className="mt-10 flex items-center justify-between">
            <span className="text-[11px] text-slate-400">{t('auth:footer')}</span>
            <LangSelect />
          </div>
        </div>
      </main>
    </div>
  )
}

function GoogleMark() {
  return (
    <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.3 17.7 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-3.2-.4-4.6H24v9.1h12.4c-.5 2.9-2.2 5.3-4.7 7l7.6 5.9c4.4-4.1 6.8-10.1 6.8-17.4z" />
      <path fill="#FBBC05" d="M10.4 28.7c-.5-1.4-.8-2.9-.8-4.7s.3-3.3.8-4.7l-7.8-6.1C.9 16.5 0 20.1 0 24s.9 7.5 2.6 10.8l7.8-6.1z" />
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.6-5.9c-2.1 1.4-4.8 2.3-8.3 2.3-6.3 0-11.7-3.8-13.6-9.8l-7.8 6.1C6.5 42.6 14.6 48 24 48z" />
    </svg>
  )
}
